import {
    Box,
    Collapse,
    Flex,
    HStack,
    RadioGroup,
    Stack,
    useColorMode,
    VStack
} from '@chakra-ui/react'
import { Body, Toggle, useFormContext } from '@missionlabs/react'
import { NotAvailable, OutOfHoursForwardType } from '@missionlabs/types'
import { Schedule } from 'features/settings/pages/Calls/OutOfHoursSetting/Schedule/Schedule'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { RedirectExternalRadio } from 'shared/components/OutOfHours/RedirectExternalRadio'
import { RedirectInternalRadio } from 'shared/components/OutOfHours/RedirectInternalRadio'
import { VoicemailNoMessageRadio } from 'shared/components/OutOfHours/VoicemailNoMessageRadio'
import { VoicemailRadio } from 'shared/components/OutOfHours/VoicemailRadio'
import { useOutOfHoursMethods } from 'shared/hooks/useOutOfHoursMethods'

import { AddEditTeamFields } from './addEditTeamSchema'

interface TeamsOutOfHoursProps {}

export const TeamsOutOfHours: React.FC<TeamsOutOfHoursProps> = () => {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()

    const { setValue, watch, setError, clearErrors } = useFormContext<AddEditTeamFields>()
    const { callRoute, updateValues } = useOutOfHoursMethods<AddEditTeamFields>(watch, setValue)

    const onUpdate = useCallback(
        (update?: NotAvailable) => {
            const updatedNotAvailable = {
                ...watch('notAvailable'),
                ...update
            }

            setValue('notAvailable', updatedNotAvailable, {
                shouldDirty: true
            })
        },
        [setValue, watch]
    )

    const onNumberValidation = useCallback(
        (isValidNumber: boolean) => {
            const fieldName = 'outOfHoursRedirect'
            if (callRoute.outOfHours !== 'redirect') return

            if (!isValidNumber) {
                setError(fieldName, { type: 'validate', message: t('validations.number') })
            } else {
                clearErrors(fieldName)
            }
        },
        [clearErrors, setError, callRoute.outOfHours, t]
    )

    return (
        <>
            <Flex direction="column" align="flex-start" id="out-of-hours">
                <VStack align="flex-start" gap={2} w="100%">
                    <HStack justify="space-between" w="100%">
                        <Body variant="bold">{t('calls.outOfHours')}</Body>
                        <Toggle
                            isChecked={watch('notAvailable.enabled')}
                            onChange={e =>
                                setValue('notAvailable.enabled', e.target.checked, {
                                    shouldDirty: true
                                })
                            }
                        />
                    </HStack>
                    <Body size="sm" variant="bold" color={`${colorMode}.tones.stormGrey`}>
                        {t('calls.certainTimes')}
                    </Body>
                </VStack>
                <Box
                    as={Collapse}
                    in={watch('notAvailable.enabled')}
                    w="100%"
                    startingHeight="0px"
                    mt={4}
                >
                    <Schedule notAvailable={watch('notAvailable')} onUpdate={onUpdate} />
                    <Box maxW="100%" mt={4} ml={1}>
                        <RadioGroup
                            value={callRoute.outOfHours}
                            onChange={(value: OutOfHoursForwardType) => {
                                setValue('callRoute.outOfHours', value, {
                                    shouldDirty: true
                                })
                            }}
                            alignSelf="start"
                        >
                            <Body
                                w="full"
                                textAlign="start"
                                py="8px"
                                size="sm"
                                variant="bold"
                                color={`${colorMode}.tones.stormGrey`}
                            >
                                {t('calls.whatHappens')}
                            </Body>
                            <Stack spacing={1}>
                                <VoicemailRadio
                                    uploadType="shared"
                                    type="outOfHours"
                                    forwardType={callRoute.outOfHours}
                                    callRoute={callRoute}
                                    onUpdate={update => updateValues('outOfHoursVoicemail', update)}
                                />
                                <VoicemailNoMessageRadio
                                    uploadType="shared"
                                    type="outOfHours"
                                    callRoute={callRoute}
                                    forwardType={callRoute.outOfHours}
                                    onUpdate={update => updateValues('outOfHoursVoicemail', update)}
                                />
                                <RedirectInternalRadio
                                    type="outOfHours"
                                    forwardType={callRoute.outOfHours}
                                    callRoute={callRoute}
                                    onUpdate={update =>
                                        updateValues('outOfHoursRedirectInternal', update)
                                    }
                                />
                                <RedirectExternalRadio
                                    type="outOfHours"
                                    forwardType={callRoute.outOfHours}
                                    callRoute={callRoute}
                                    presentAlways={false}
                                    onUpdate={update => updateValues('outOfHoursRedirect', update)}
                                    onNumberValidation={onNumberValidation}
                                />
                            </Stack>
                        </RadioGroup>
                    </Box>
                </Box>
            </Flex>
        </>
    )
}
